import { ByocService } from '@app/byoc/popup/service/byoc.service';
import { Injectable } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormControl } from '@angular/forms';
import { Course, Mission } from '@app/core/model/library.model';
import { Subject, Observable, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { group } from '../../utils';

@Injectable()
export class ByocMissionService {
    /**
     * Step of Adding
     */
    step!: 'general' | 'course' | 'position';

    /**
     * Processing Something
     */
    processing!: boolean;

    /**
     * Form
     */
    form!: UntypedFormGroup;

    /**
     * If URL is fetchable
     */
    infoLoaded!: boolean;

    /**
     * If URL can be loaded in an iframe
     */
    iframe!: boolean;

    /**
     * If URL can be loaded in an iframe
     */
    skipSteps: string[] = [];

    mission!: Mission;

    isSubmodal = false;

    selectedCoursesFullData: Course[] = [];

    /**
     * Is mission name exists
     */
    nameExists = false;

    /**
     * Mission name check subject
     */
    nameCheckSubject = new Subject<{ isChecking: boolean; nameExists: boolean }>();

    /**
     * Subscriptions
     */
    private subscriptions: Subscription = new Subscription();

    /**
     * Closing subject
     */
    private closingSubject = new Subject<string>();

    /**
     * Subscription
     */
    private subscription?: Subscription;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private byocService: ByocService,
    ) {
        this.initForm();
        this.subscriptions.add(
            this.form!.get('name')!
                .valueChanges.pipe(debounceTime(200))
                .subscribe(() => {
                    this.checkName();
                }),
        );
    }

    destroy() {
        this.subscriptions.unsubscribe();
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    get value() {
        return this.form.getRawValue();
    }

    /**
     * Close Emit Event
     */
    close(reason: string): void {
        this.closingSubject.next(reason);
    }

    /**
     * Closing Observable
     */
    get closeEvent(): Observable<string> {
        return this.closingSubject.asObservable();
    }

    get coursesFormGroup() {
        return group(this.form.get('courses'));
    }

    get selectedCoursesNumber() {
        const coursesPlainData = this.coursesFormGroup.value;
        return Object.keys(coursesPlainData).filter((key) => coursesPlainData[key]).length;
    }

    /**
     * Check mission name data
     */
    get checkNameData$() {
        return this.nameCheckSubject.asObservable();
    }

    addCourseControl(controlId: string, value = false): void {
        // eslint-disable-next-line no-prototype-builtins
        if ((<object>this.coursesFormGroup.value).hasOwnProperty(controlId)) {
            return;
        }
        this.coursesFormGroup.addControl(controlId, new UntypedFormControl(value));
    }

    removeAllCourseControls(): void {
        const coursesIds = Object.keys(this.coursesFormGroup.controls);

        coursesIds.forEach((key) => {
            this.coursesFormGroup.removeControl(key);
        });
    }

    /**
     * Patch Form with courseId
     */
    // courses(courses: any) {
    //   if (!courses) {
    //     return;
    //   }
    //   this.form.patchValue({
    //     courses: courses
    //   }, {emitEvent: false});
    // }

    /**
     *
     * @param courses list of courses with all data
     */
    appendSelectedCoursesFullData(courses: Course[]) {
        const coursesPlainData = this.coursesFormGroup.value;
        const selectedCoursesIds = Object.keys(coursesPlainData).filter((key) => coursesPlainData[key]);
        const selectedCoursesFullIds: string[] = this.selectedCoursesFullData.map((course) => course.id);
        const coursesToAppend = courses
            .filter((course) => selectedCoursesIds.includes(course.id))
            .filter((course) => !selectedCoursesFullIds.includes(course.id));

        this.selectedCoursesFullData = this.selectedCoursesFullData.concat(coursesToAppend);
    }

    /**
     * Check mission name
     */
    private checkName(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }

        this.nameCheckSubject.next({ isChecking: true, nameExists: this.nameExists });
        this.subscription = this.byocService.checkMissionNameOnCreate(this.form.value.name).subscribe((exists: boolean) => {
            this.nameExists = exists;
            this.nameCheckSubject.next({ isChecking: false, nameExists: exists });
        });
    }

    /**
     * Init Form with all fields
     */
    private initForm(): void {
        this.form = this.formBuilder.group({
            name: ['', [Validators.required, Validators.pattern(/^(?!\s*$).+/), Validators.maxLength(55)]],
            color: ['', Validators.required],
            description: ['', [Validators.required, Validators.pattern(/^(?!\s*$).+/)]],
            courses: this.formBuilder.group({}),
            status: ['', Validators.required],
        });
    }
}
